import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { produce, type ChatModel } from "@acme/util";
import { chatSessionActiveAtom } from "./chat";

/**
 * Compare mode: when set, every message in a local chat is also answered by
 * this model, shown side by side with the chat's own model. Null = off.
 */
export const compareModelAtom = atomWithStorage<ChatModel | null>('compareModel', null);

/**
 * "Use this answer": make a compare reply the one that stays in the
 * conversation. The two replies swap roles, so later turns are sent with the
 * chosen reply as context and the other one becomes the side-by-side option.
 */
export const chatUseCompareReplyAtom = atom(
    null,
    (get, set, compareMessageId: string) => {
        const session = get(chatSessionActiveAtom);
        if (!session) return;
        const chosen = session.messages.find(m => m.id === compareMessageId);
        if (!chosen?.isCompare) return;

        // The reply currently kept for the same user message (latest wins, as
        // in the chat view).
        const kept = session.messages
            .filter(m => m.parentId === chosen.parentId && !m.isCompare && m.senderRole === 'assistant')
            .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0];

        set(chatSessionActiveAtom, produce(session, draft => {
            for (const m of draft.messages) {
                if (m.id === chosen.id) m.isCompare = false;
                else if (kept && m.id === kept.id) m.isCompare = true;
            }
        }));
    }
);
